// UploadDocumentsSection.js - Complete Upload Implementation
import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { API_BASE } from '../services/api';
import { FaUpload, FaFile, FaTrash, FaEye, FaDownload, FaFolder, FaChevronDown } from 'react-icons/fa';

const UploadDocumentsSection = ({ user, selectedRegNumber }) => {
  const [regNumber, setRegNumber] = useState('');
  const [docType, setDocType] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('upload');
  const [expandedFolders, setExpandedFolders] = useState({});

  // Document types
  const documentTypes = [
    { value: 'registration', label: '🚗 Vehicle Registration' },
    { value: 'insurance', label: '🛡️ Insurance Document' },
    { value: 'mot', label: '✅ MOT Certificate' },
    { value: 'service', label: '🔧 Service Record' },
    { value: 'invoice', label: '💰 Invoice/Receipt' },
    { value: 'damage_photo', label: '📸 Damage Photo' },
    { value: 'owner_id', label: '🪪 Owner ID Proof' },
    { value: 'other', label: '📄 Other Document' }
  ];

  // Load uploaded documents
  const loadDocuments = async () => {
    setLoading(true);
    try {
      const docs = await api.getUploadedDocuments();
      setUploadedDocs(docs || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  // Auto-open folder OR pre-fill registration when selectedRegNumber is provided
  useEffect(() => {
    if (selectedRegNumber) {
      const checkAndHandle = async () => {
        try {
          const docs = await api.getUploadedDocuments();
          const regDocs = docs.filter(doc => doc.regNumber === selectedRegNumber);

          if (regDocs.length > 0) {
            setViewMode('view');
            setExpandedFolders(prev => ({
              ...prev,
              [selectedRegNumber]: true
            }));

            setTimeout(() => {
              const folderElement = document.getElementById(`folder-${selectedRegNumber}`);
              if (folderElement) {
                folderElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                folderElement.classList.add('highlighted');
                setTimeout(() => folderElement.classList.remove('highlighted'), 2000);
              }
            }, 100);
          } else {
            setViewMode('upload');
            setRegNumber(selectedRegNumber);
            setMessage(`📤 Upload documents for ${selectedRegNumber}`);

            setTimeout(() => {
              const docTypeSelect = document.getElementById('doc-type');
              if (docTypeSelect) docTypeSelect.focus();
            }, 100);
          }
        } catch (error) {
          console.error('Error checking documents:', error);
          setViewMode('upload');
          setRegNumber(selectedRegNumber);
        }
      };

      checkAndHandle();
    }
  }, [selectedRegNumber]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  // Remove file from selection
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload documents
  const handleUpload = async () => {
    if (!regNumber.trim()) {
      setMessage('❌ Please enter vehicle registration number');
      return;
    }

    if (!docType) {
      setMessage('❌ Please select document type');
      return;
    }

    if (selectedFiles.length === 0) {
      setMessage('❌ Please select at least one file');
      return;
    }

    setUploading(true);
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('regNumber', regNumber.toUpperCase().trim());
      formData.append('docType', docType);
      formData.append('uploadedBy', user.username);
      formData.append('uploadedByName', `${user.firstName} ${user.lastName}`.trim());

      selectedFiles.forEach((file) => {
        formData.append('documents', file);
      });

      await api.uploadDocuments(formData);

      setMessage(`✅ ${selectedFiles.length} document(s) uploaded successfully!`);
      setRegNumber('');
      setDocType('');
      setSelectedFiles([]);
      loadDocuments();

      const fileInput = document.getElementById('file-input');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      setMessage(`❌ Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Delete document
  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await api.deleteDocument(docId);
      setMessage('✅ Document deleted successfully');
      loadDocuments();
    } catch (error) {
      setMessage(`❌ Delete failed: ${error.message}`);
    }
  };

  // View document
  const handleView = (doc) => {
    const fullUrl = doc.url.startsWith('http')
      ? doc.url
      : `${API_BASE}${doc.url}`;

    console.log('📂 Opening document:', fullUrl);
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  // Toggle folder collapse
  const toggleFolder = (reg) => {
    setExpandedFolders(prev => ({
      ...prev,
      [reg]: !prev[reg]
    }));
  };

  // Group documents by registration
  const groupedDocs = uploadedDocs.reduce((acc, doc) => {
    const reg = doc.regNumber;
    if (!acc[reg]) {
      acc[reg] = [];
    }
    acc[reg].push(doc);
    return acc;
  }, {});

  return (
    <section className="content-section upload-section">
      <div className="upload-header">
        <h2>📤 Upload Documents</h2>
        <div className="view-toggle">
          <button
            className={`btn ${viewMode === 'upload' ? 'btn-primary' : ''}`}
            onClick={() => setViewMode('upload')}
          >
            Upload New
          </button>
          <button
            className={`btn ${viewMode === 'view' ? 'btn-primary' : ''}`}
            onClick={() => setViewMode('view')}
          >
            View Uploaded
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {/* UPLOAD MODE */}
      {viewMode === 'upload' && (
        <div className="upload-form">
          <div className="form-group">
            <label htmlFor="reg-number">
              🚗 Vehicle Registration Number <span className="required">*</span>
            </label>
            <input
              id="reg-number"
              type="text"
              placeholder="e.g., ABC123, XYZ789"
              value={regNumber}
              onChange={(e) => setRegNumber(e.target.value.toUpperCase())}
              className="form-input"
            />
            <small className="help-text">Enter the vehicle registration number to organize documents</small>
          </div>

          <div className="form-group">
            <label htmlFor="doc-type">
              📄 Document Type <span className="required">*</span>
            </label>
            <select
              id="doc-type"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="form-select"
            >
              <option value="">-- Select Document Type --</option>
              {documentTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="file-input">
              📎 Select Files <span className="required">*</span>
            </label>
            <div className="file-input-wrapper">
              <input
                id="file-input"
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={handleFileSelect}
                className="file-input"
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => document.getElementById('file-input').click()}
              >
                <FaUpload /> Choose Files
              </button>
              <span className="file-count">
                {selectedFiles.length > 0 ? `${selectedFiles.length} file(s) selected` : 'No files selected'}
              </span>
            </div>
            <small className="help-text">Allowed: JPG, PNG, PDF | Max 5MB per file</small>
          </div>

          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="selected-files">
              <h4>Selected Files:</h4>
              <div className="files-grid">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="file-card">
                    <div className="file-icon">
                      {file.type.includes('pdf') ? '📄' : '🖼️'}
                    </div>
                    <div className="file-info">
                      <p className="file-name">{file.name}</p>
                      <p className="file-size">{(file.size / 1024).toFixed(2)} KB</p>
                    </div>
                    <button
                      className="remove-btn"
                      onClick={() => removeFile(index)}
                      title="Remove file"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              className="btn btn-primary btn-upload"
              onClick={handleUpload}
              disabled={uploading || !regNumber || !docType || selectedFiles.length === 0}
            >
              {uploading ? '⏳ Uploading...' : '📤 Upload Documents'}
            </button>
          </div>
        </div>
      )}

      {/* VIEW MODE */}
      {viewMode === 'view' && (
        <div className="uploaded-docs">
          {loading ? (
            <div className="loading-state">Loading documents...</div>
          ) : Object.keys(groupedDocs).length === 0 ? (
            <div className="empty-state">
              <FaFolder size={50} color="#ccc" />
              <p>No documents uploaded yet</p>
            </div>
          ) : (
            <div className="docs-by-registration">
              {Object.entries(groupedDocs).map(([reg, docs]) => (
                <div key={reg} id={`folder-${reg}`} className="registration-folder">
                  <div
                    className={`folder-header ${!expandedFolders[reg] ? 'collapsed' : ''}`}
                    onClick={() => toggleFolder(reg)}
                  >
                    <FaChevronDown className="collapse-icon" size={16} />
                    <FaFolder color="#FFB800" size={24} />
                    <h3>{reg}</h3>
                    <span className="doc-count">{docs.length} document(s)</span>
                  </div>
                  <div className={`folder-contents ${!expandedFolders[reg] ? 'collapsed' : ''}`}>
                    <table className="docs-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>File Name</th>
                          <th>Uploaded By</th>
                          <th>Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {docs.map(doc => (
                          <tr key={doc._id}>
                            <td>
                              <span className="doc-type-badge">
                                {documentTypes.find(t => t.value === doc.docType)?.label || doc.docType}
                              </span>
                            </td>
                            <td className="file-name-cell">
                              {doc.fileType === 'pdf' ? '📄' : '🖼️'} {doc.fileName}
                            </td>
                            <td>{doc.uploadedByName || doc.uploadedBy}</td>
                            <td>{new Date(doc.uploadedAt).toLocaleDateString()}</td>
                            <td className="actions-cell">
                              <button
                                className="icon-btn view-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleView(doc);
                                }}
                                title="View Document"
                                type="button"
                              >
                                <FaEye />
                              </button>
                              <button
                                className="icon-btn delete-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(doc._id);
                                }}
                                title="Delete Document"
                                type="button"
                              >
                                <FaTrash />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default UploadDocumentsSection;
